import { useEffect, useState } from 'react';
import { Button, Input, Text, YStack, XStack, Spinner, Row, H2, Icon } from '@holo/ui';
import SEO from '@holo/seo';
import { serverSideTranslations } from '@holo/i18n/serverSideTranslations';
import type { GetStaticProps } from 'next';
import { toast } from 'sonner';
import { signIn } from '@holo/auth-next/react';
import { useRouter } from 'next/router';
import { useTranslation } from '@holo/i18n';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from '@holo/link';

type LoginFormData = z.infer<typeof formValidationSchema>;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: await serverSideTranslations(locale),
  };
};

const formValidationSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(4, { message: 'Must be at least 4 characters' }),
});

const Email = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { return_to: returnTo } = router.query;

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const hideSpinner = () => setShowSpinner(false);
    router.events.on('routeChangeComplete', hideSpinner);

    return () => {
      router.events.off('routeChangeComplete', hideSpinner);
    };
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(formValidationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    setShowSpinner(true);

    const response = await signIn('credentials', {
      username: data.email,
      password: data.password,
      redirect: false,
      type: 'CUSTOMER',
    });

    if (response?.error) {
      setShowSpinner(false);

      toast.error(t('login.errorTitle'), {
        description: t('login.errorDescription'),
        icon: <Icon icon="alertTriangle" size={16} />,
      });

      return;
    }

    if (response?.ok) {
      if (returnTo) {
        router.push(`/${returnTo}`);
      } else {
        router.push('/');
      }
    }
  });

  return (
    <YStack minHeight="100vh" jc="center" ai="center">
      <SEO
        title={t('login.title')}
        description={t('login.description')}
        openGraph={{
          type: 'website',
          title: t('login.title'),
          description: t('login.description'),
        }}
      />

      <YStack $md={{ maxWidth: 335 }} w="100%" paddingHorizontal="$5">
        <H2 textAlign="center" mb="$7">
          Log in to Holo
        </H2>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <Row padding={0} borderBottomWidth={0} marginBottom="$4">
            <Input
              // Remove `id`, use testId and change in `global-setup.ts` when this is resolved: https://github.com/tamagui/tamagui/issues/467
              id="email"
              testID="login-email-input"
              label={t('login.email')}
              onSubmitEditing={onSubmit}
              name="email"
              placeholder={t('login.placeholderEmail')}
              autoComplete="email"
              keyboardType="email-address"
              control={control}
              borderColor={errors.email ? '$error' : '$border'}
            />
          </Row>

          <Row padding={0} borderBottomWidth={0}>
            <Input
              id="password"
              // Remove `id`, use testId and change in `global-setup.ts` when this is resolved: https://github.com/tamagui/tamagui/issues/467
              testID="login-password-input"
              onSubmitEditing={onSubmit}
              name="password"
              label={t('login.password')}
              placeholder={t('login.placeholderPassword')}
              type="password"
              control={control}
              borderColor={errors.password ? '$error' : '$border'}
            />
          </Row>

          <Button
            mt="$8"
            mb="$4"
            type="submit"
            width="100%"
            fontSize="$16"
            backgroundColor="$contrast"
            hoverStyle={{ backgroundColor: '$contrast' }}
            focusStyle={{ backgroundColor: '$contrast' }}
            color="$background"
            disabled={showSpinner}
            testID="login-submit-button"
          >
            {showSpinner ? <Spinner size="small" color="white" /> : t('login.signIn')}
          </Button>

          <XStack borderTopWidth={1} borderColor="$border" jc="center" mt="$5" pt="$5">
            <Link href={returnTo ? `/login?return_to=${returnTo}` : '/login'}>
              <Text>← Other Login Options</Text>
            </Link>
          </XStack>

          {/* <YStack marginTop={20} alignItems="center">
            <Link href="/reset-password">
              <Text textDecorationLine="underline" color="$darkGray">
                {t('login.forgot')}
              </Text>
            </Link>
          </YStack> */}
        </form>
      </YStack>
    </YStack>
  );
};

export default Email;
